import React from 'react';
import { Layout, Grid, AlertTriangle, Settings, X } from 'lucide-react';
import AlignmentGuides from './AlignmentGuides';
import CanvasElement from './CanvasElement';

const CanvasArea = ({
  reviewReadOnly = false,
  screens,
  currentScreen,
  currentScreenIndex,
  canvasZoom,
  canvasPan,
  isPanning,
  showGrid,
  gridSize,
  showAlignmentGuides,
  showPositionOverlay,
  showProperties,
  setShowProperties,
  showOutOfBoundsWarning,
  setShowOutOfBoundsWarning,
  selectedElement,
  setSelectedElement,
  selectedElements,
  editingElement,
  setEditingElement,
  editingValue,
  setEditingValue,
  dragging,
  resizing,
  draggingFromSidebar,
  setDraggingFromSidebar,
  dragPreviewPosition,
  setDragPreviewPosition,
  draggingScreenIndex,
  dragOverScreenIndex,
  setDragOverScreenIndex,
  isElementLocked,
  handleCanvasPanStart,
  handleMouseDown,
  handleDoubleClick,
  handleInlineEditSave,
  handleInlineEditCancel,
  handleResizeStart,
  handleScreenDrop,
  updateSubElementPosition,
  updateSubElementSize,
  updateResponseConfig,
  snapPosition,
  addNewScreen,
  addElementToScreen,
  fitElementsToCanvas,
  getOutOfBoundsElements
}) => {
  // Responsive canvases are designed on a stable artboard at their nominal
  // design width — exactly like a fixed device canvas. The "fluid" behaviour
  // (uniformly scaling to fill the screen) happens only at render time in
  // ScreenRenderer (preview + participant). Keeping the builder coordinate
  // system fixed means element positions are unambiguous and never shift as
  // side panels open/close — the design width is the responsive reference
  // width that the canvas scales from (FECOA-1532).
  const isResponsiveCanvas = currentScreen?.layout?.deviceMode === 'responsive';
  const canvasRenderWidth = currentScreen?.layout?.width || 800;
  const canvasRenderHeight = currentScreen?.layout?.height || 600;

  return (
    <div
      className={`flex-1 overflow-auto bg-gray-100 relative force-light-mode ${reviewReadOnly ? 'pointer-events-none' : ''}`}
      style={{ cursor: isPanning ? 'grabbing' : 'default' }}
      onMouseDown={handleCanvasPanStart}
      onContextMenu={(e) => e.preventDefault()}
      onDragOver={(e) => {
        if (draggingScreenIndex === null) return;
        e.preventDefault();
        
        const tabBar = document.querySelector('[data-screen-index]')?.closest('.flex.items-center.space-x-2');
        if (!tabBar) return;
        
        const rect = tabBar.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        
        const tabs = tabBar.querySelectorAll('[data-screen-index]');
        let closestIndex = screens.length;
        let minDistance = Infinity;
        
        tabs.forEach((tab) => {
          const tabRect = tab.getBoundingClientRect();
          const tabCenterX = tabRect.left + tabRect.width / 2 - rect.left;
          const distance = Math.abs(relativeX - tabCenterX);
          
          if (distance < minDistance) {
            minDistance = distance;
            const index = parseInt(tab.getAttribute('data-screen-index'));
            
            if (relativeX < tabCenterX) {
              closestIndex = index;
            } else {
              closestIndex = index + 1;
            }
          }
        });
        
        if (closestIndex !== draggingScreenIndex && closestIndex !== dragOverScreenIndex) {
          setDragOverScreenIndex(closestIndex);
        }
      }}
      onDrop={(e) => {
        if (dragOverScreenIndex !== null) {
          handleScreenDrop(e, dragOverScreenIndex);
        }
      }}
    >
      {/* No Screens Empty State */}
      {screens.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <Layout className="mx-auto mb-3 h-6 w-6 text-gray-400" strokeWidth={1.75} />
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              No Screens Yet
            </h3>
            <p className="text-sm leading-relaxed text-gray-500 mb-5">
              Get started by adding your first screen. Screens help you organize your assessment into logical pages or sections.
            </p>
            <button
              onClick={addNewScreen}
              className="btn btn-primary"
            >
              <Layout className="w-4 h-4" />
              Add Your First Screen
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Canvas Size Indicator */}
          {currentScreen && (
            <div className="absolute top-4 left-4 z-[5] rounded-md border border-gray-200 bg-white px-2 py-1 font-mono text-[11.5px] font-medium text-gray-600 shadow-xs">
              {isResponsiveCanvas
                ? `Canvas: Responsive · ${currentScreen.layout.width} × ${currentScreen.layout.height} ref · scales to fill`
                : `Canvas: ${currentScreen.layout.width} × ${currentScreen.layout.height}px`}
            </div>
          )}
          
          {/* Out of Bounds Warning Banner */}
          {showOutOfBoundsWarning && currentScreen && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30">
              <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-900 shadow-float">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
                <div className="text-[13px]">
                  <span className="font-semibold">Elements outside canvas</span>
                  <span className="text-amber-700 ml-2">
                    {getOutOfBoundsElements().length} element{getOutOfBoundsElements().length !== 1 ? 's' : ''} need repositioning
                  </span>
                </div>
                <button
                  onClick={() => fitElementsToCanvas('proportional')}
                  className="btn btn-primary btn-sm ml-1"
                >
                  Auto-Fit
                </button>
                <button
                  onClick={() => setShowOutOfBoundsWarning(false)}
                  className="rounded-md p-1 text-amber-700/70 hover:bg-amber-100 hover:text-amber-900 transition-colors"
                  title="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Quick Properties Toggle (when hidden) */}
          {!showProperties && selectedElement && currentScreen?.elements.some(el => el.id === selectedElement) && (
            <div className="absolute top-4 right-4 z-20 max-w-xs rounded-xl border border-gray-200 bg-white p-3.5 shadow-float">
              <div className="flex items-start gap-2.5">
                <Settings className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-gray-900 mb-1">Element Selected</p>
                  <p className="text-xs leading-relaxed text-gray-500 mb-3">
                    Click "Show Properties" to edit this element's styling and properties.
                  </p>
                  <button
                    onClick={() => setShowProperties(true)}
                    className="btn btn-primary btn-sm"
                  >
                    Show Properties Panel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Scrollable canvas wrapper */}
          <div 
            className="p-8 flex items-center justify-center min-w-full min-h-full canvas-container"
            style={{
              cursor: isPanning ? 'grabbing' : 'default'
            }}
          >
            {/* Scaled canvas container - accounts for zoom transform */}
            <div 
              className="canvas-background"
              style={{
                width: `${canvasRenderWidth * canvasZoom}px`,
                height: `${canvasRenderHeight * canvasZoom}px`,
                position: 'relative',
                transform: `translate(${canvasPan.x}px, ${canvasPan.y}px)`,
                transition: isPanning ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              <div
                id="canvas-area"
                className="bg-white shadow-lg rounded-lg relative overflow-hidden"
                onClick={(e) => {
                  const isCanvasBackground = e.target.id === 'canvas-area';
                  const isNotOnElement = !e.target.closest('[data-element]');
                  
                  if (isCanvasBackground && isNotOnElement) {
                    setSelectedElement(null);
                  }
                }}
                onDragOver={(e) => {
                  if (draggingFromSidebar) {
                    e.preventDefault();
                    
                    const canvas = document.getElementById('canvas-area');
                    const rect = canvas.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / canvasZoom;
                    const y = (e.clientY - rect.top) / canvasZoom;
                    
                    setDragPreviewPosition({
                      x: snapPosition(Math.max(0, x - 60)),
                      y: snapPosition(Math.max(0, y - 22))
                    });
                  } else {
                    console.log('⚠️ [DRAG OVER] No draggingFromSidebar state');
                  }
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget === e.target) {
                    setDragPreviewPosition(null);
                  }
                }}
                onDrop={(e) => {
                  console.log('📦 [DROP] Drop event triggered, draggingFromSidebar:', draggingFromSidebar);
                  if (draggingFromSidebar) {
                    e.preventDefault();
                    const canvas = document.getElementById('canvas-area');
                    const rect = canvas.getBoundingClientRect();
                    
                    const x = (e.clientX - rect.left) / canvasZoom;
                    const y = (e.clientY - rect.top) / canvasZoom;
                    
                    const position = {
                      x: snapPosition(Math.max(0, x - 60)),
                      y: snapPosition(Math.max(0, y - 22))
                    };
                    
                    console.log('✅ [DROP] Creating element:', draggingFromSidebar.type, 'at position:', position);
                    addElementToScreen(
                      draggingFromSidebar.type,
                      draggingFromSidebar.item,
                      position
                    );
                    
                    setDraggingFromSidebar(null);
                    setDragPreviewPosition(null);
                  } else {
                    console.log('❌ [DROP] No draggingFromSidebar state!');
                  }
                }}
                style={{
                  width: `${canvasRenderWidth}px`,
                  height: `${canvasRenderHeight}px`,
                  transform: `scale(${canvasZoom})`,
                  transformOrigin: 'top left',
                  backgroundColor: currentScreen?.layout.background || '#ffffff',
                  backgroundImage: showGrid 
                    ? `repeating-linear-gradient(0deg, transparent, transparent ${gridSize - 1}px, #e5e7eb ${gridSize - 1}px, #e5e7eb ${gridSize}px),
                       repeating-linear-gradient(90deg, transparent, transparent ${gridSize - 1}px, #e5e7eb ${gridSize - 1}px, #e5e7eb ${gridSize}px)`
                    : 'none',
                  backgroundSize: showGrid ? `${gridSize}px ${gridSize}px` : 'auto',
                  transition: 'transform 0.2s ease'
                }}
              >
                {showAlignmentGuides && <AlignmentGuides currentScreen={currentScreen} />}
                {currentScreen?.elements.map((element) => (
                  <CanvasElement
                    key={element.id}
                    element={element}
                    selectedElement={selectedElement}
                    setSelectedElement={setSelectedElement}
                    isSelected={selectedElement === element.id || (selectedElements?.has?.(element.id) ?? false)}
                    editingElement={editingElement}
                    setEditingElement={setEditingElement}
                    editingValue={editingValue}
                    setEditingValue={setEditingValue}
                    dragging={dragging}
                    resizing={resizing}
                    showPositionOverlay={showPositionOverlay}
                    isElementLocked={isElementLocked}
                    handleMouseDown={handleMouseDown}
                    handleDoubleClick={handleDoubleClick}
                    handleInlineEditSave={handleInlineEditSave}
                    handleInlineEditCancel={handleInlineEditCancel}
                    handleResizeStart={handleResizeStart}
                    updateSubElementPosition={updateSubElementPosition}
                    updateSubElementSize={updateSubElementSize}
                    updateResponseConfig={updateResponseConfig}
                    snapPosition={snapPosition}
                  />
                ))}

                {currentScreen?.elements.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <Grid className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Drag elements from the sidebar to start building</p>
                    </div>
                  </div>
                )}

                {/* Drag Preview Overlay */}
                {draggingFromSidebar && dragPreviewPosition && (
                  <div
                    className="absolute border-2 border-dashed border-primary-500 bg-primary-50 bg-opacity-40 rounded pointer-events-none"
                    style={{
                      left: `${dragPreviewPosition.x}px`,
                      top: `${dragPreviewPosition.y}px`,
                      width: draggingFromSidebar.type === 'question' ? '500px' : 
                             draggingFromSidebar.type === 'text' ? '400px' : 
                             draggingFromSidebar.type === 'button' ? '120px' :
                             draggingFromSidebar.type === 'divider' ? '400px' : '400px',
                      height: draggingFromSidebar.type === 'question' ? '200px' :
                              draggingFromSidebar.type === 'text' ? '40px' :
                              draggingFromSidebar.type === 'button' ? '40px' :
                              draggingFromSidebar.type === 'divider' ? '2px' : '200px',
                      transition: 'all 0.1s ease-out'
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-700 bg-white px-2 py-1 rounded shadow-sm">
                        {draggingFromSidebar.type === 'question' ? 'Question' :
                         draggingFromSidebar.type === 'text' ? 'Text' :
                         draggingFromSidebar.type === 'button' ? 'Button' :
                         draggingFromSidebar.type === 'image' ? 'Image' :
                         draggingFromSidebar.type === 'divider' ? 'Divider' :
                         draggingFromSidebar.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CanvasArea;
